import { useState } from 'react';

import { useCreateCustomer, useUpdateCustomer } from '../../api/hooks';
import type { CustomerView } from '../../api/types';
import { Button, FormField, Modal, TextInput, useToast } from '../../components/DesignSystem';
import { errorMessage } from '../../lib/errors';

interface CustomerModalProps {
  /** Existing customer to edit, or null to create a new one. */
  customer: CustomerView | null;
  onClose: () => void;
  /** Called with the saved customer id (used by the deal form). */
  onSaved?: (customerId: string) => void;
}

/** Create/edit customer form in a modal. */
export function CustomerModal({ customer, onClose, onSaved }: CustomerModalProps) {
  const { showToast } = useToast();
  const createCustomer = useCreateCustomer();
  const updateCustomer = useUpdateCustomer();

  const [name, setName] = useState(customer?.name ?? '');
  const [buyerType, setBuyerType] = useState(customer?.buyerType ?? '');
  const [territory, setTerritory] = useState(customer?.territory ?? '');

  const isPending = createCustomer.isPending || updateCustomer.isPending;

  async function handleSave() {
    if (name.trim() === '') {
      showToast('Customer name is required', 'error');
      return;
    }

    const input = {
      name: name.trim(),
      ...(buyerType.trim() !== '' ? { buyerType: buyerType.trim() } : {}),
      ...(territory.trim() !== '' ? { territory: territory.trim() } : {}),
    };

    try {
      const saved = customer
        ? await updateCustomer.mutateAsync({ customerId: customer.id, input })
        : await createCustomer.mutateAsync(input);
      showToast(customer ? 'Customer updated' : 'Customer created');
      onSaved?.(saved.id);
      onClose();
    } catch (error) {
      showToast(errorMessage(error, 'Could not save customer'), 'error');
    }
  }

  return (
    <Modal
      actions={
        <>
          <Button disabled={isPending} onClick={handleSave} type="button">
            {isPending ? 'Saving…' : customer ? 'Save changes' : 'Create Customer'}
          </Button>
          <Button onClick={onClose} type="button" variant="secondary">
            Cancel
          </Button>
        </>
      }
      title={customer ? 'Edit Customer' : 'New Customer'}
    >
      <div className="rfp-stack">
        <FormField htmlFor="customer-name" label="Name">
          <TextInput
            id="customer-name"
            onChange={(event) => setName(event.target.value)}
            placeholder="e.g. NordStream Media"
            value={name}
          />
        </FormField>
        <FormField htmlFor="customer-buyer-type" label="Buyer Type">
          <TextInput
            id="customer-buyer-type"
            onChange={(event) => setBuyerType(event.target.value)}
            placeholder="e.g. Broadcaster, SVOD"
            value={buyerType}
          />
        </FormField>
        <FormField htmlFor="customer-territory" label="Territory">
          <TextInput
            id="customer-territory"
            onChange={(event) => setTerritory(event.target.value)}
            placeholder="e.g. Nordics"
            value={territory}
          />
        </FormField>
      </div>
    </Modal>
  );
}
